import { Spacing, Text } from '@vkontakte/vkui';
import unlimIllustration from 'img/quota-landing/beautifulIllustrations/unlimitedAutoupload.png';
import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PROMO_TARIFFS } from 'reactApp/appHelpers/configHelpers';
import { helpAutoUploadHref } from 'reactApp/appHelpers/featuresHelpers/features/autoUploadPromo';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getQuotaBillingSource, sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { sendAdmitadProduct } from 'reactApp/utils/admitadHelper';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';

import styles from './BeautifulIllustrations.css';

export const UnlimUploadInfo = memo(() => {
    const dispatch = useDispatch();
    const product = useSelector((state: RootState) =>
        ProductsSelectors.getProductById(state, PROMO_TARIFFS?.unlimitedUploadPromo)
    ) as Product;

    const buttonText = useMemo(() => {
        if (!product) {
            return '';
        }

        const space = product.space.value;
        const discount = product?.hasDiscount ? ' со скидкой' : '';

        return `Купить ${space}${discount}`;
    }, [product]);

    const handleClick = useCallback(() => {
        sendQuotaGa({ action: `block-click`, block_name: 'unlimited_autoupload' });
        sendTmrGoal({ id: TMR3473039, goal: 'space-add' });

        dispatch(
            openTariffBuy({
                productId: product?.id,
                source: getQuotaBillingSource(),
                checkAnonymous: true,
                onSuccess: () => sendAdmitadProduct(product),
                paySource: getPaySource('block_unlimited_autoupload'),
            })
        );
    }, [product]);

    return (
        <>
            <div className={styles.cardInfoBlock}>
                <img className={styles.unlimIllustration} src={unlimIllustration} alt="Cover image" />
                <div className={styles.cardContent}>
                    <Text className={styles.cardTitle}>
                        Как работает безлимит
                        <svg xmlns="http://www.w3.org/2000/svg" width="167" height="9" viewBox="0 0 167 9" fill="none">
                            <path
                                d="M2.99694 6.24631C65.961 -1.53765 164.589 5.43573 164.589 5.43573"
                                stroke="#B8FC75"
                                strokeWidth="4"
                                strokeLinecap="square"
                            />
                        </svg>
                    </Text>
                    <Text className={styles.cardText}>
                        Или безлимитная автозагрузка —&nbsp;это расширенная возможность обычной автозагрузки. С обычной
                        фото&nbsp;и&nbsp;видео с телефона переносятся в&nbsp;Облако и&nbsp;занимают в нём гигабайты. А с безлимитной
                        —&nbsp;нет.
                        <br />
                        Безлимит доступен в тарифах от 256 ГБ
                    </Text>
                    <Spacing size={32} />
                </div>
            </div>
            <div className={styles.cardButtonBlock}>
                {Boolean(buttonText) && (
                    <Button theme="vk" sizeMode={ButtonSizeMode.extraBig} className={styles.button} onClick={handleClick}>
                        {buttonText}
                    </Button>
                )}
                {Boolean(helpAutoUploadHref) && (
                    <div className={styles.cardLinkBlock}>
                        Подробнее{' '}
                        <a href={helpAutoUploadHref} className={styles.link} target="_blank" rel="noopener noreferrer">
                            рассказали в статье
                        </a>{' '}
                        «Как включить безлимитную
                        <br />
                        автозагрузку файлов с телефона в Облако Mail»
                    </div>
                )}
            </div>
        </>
    );
});

UnlimUploadInfo.displayName = 'UnlimUploadInfo';
